// File: webelieve-frontend/src/features/notes/stores/useNoteCollaborationStore.ts
// Zustand store for note collaboration state (scoped by noteId)

import { create } from 'zustand';
import apiClient from '../../../api/axios';
import { noteCollaborationSocket } from '../services/noteCollaborationSocket';

export interface NoteCollaborator {
  id: string;
  note_id: string;
  user_id: string;
  role: 'owner' | 'editor' | 'reviewer' | 'viewer';
  permissions: {
    canEdit: boolean;
    canComment: boolean;
    canShare: boolean;
    canManageUsers: boolean;
    canTrackChanges?: boolean;
    canAcceptReject?: boolean;
  };
  status: string;
  user_name?: string;
  user_email?: string;
  last_active?: string;
  is_owner?: boolean;
}

export interface NoteComment {
  id: string;
  note_id: string;
  user_id: string;
  comment_text: string;
  comment_type: string;
  parent_comment_id?: string | null;
  position_data?: Record<string, any> | null;
  selection_data?: Record<string, any> | null;
  resolved: boolean;
  is_resolved: boolean;
  resolved_by?: string | null;
  resolved_at?: string | null;
  user_name?: string;
  user_email?: string;
  created_at: string;
  updated_at: string;
}

export interface NoteInvitation {
  id: string;
  note_id: string;
  inviter_id: string;
  invitee_email: string;
  invitee_phone?: string | null;
  role: 'owner' | 'editor' | 'reviewer' | 'viewer';
  status: 'pending' | 'accepted' | 'declined' | 'expired' | 'revoked';
  invitation_message?: string | null;
  expires_at: string;
  accepted_at?: string | null;
  accepted_by?: string | null;
  inviter_name?: string;
  inviter_email?: string;
  invitee_name?: string;
  invitee_user_id?: string | null;
  is_expired?: boolean;
  created_at: string;
  updated_at: string;
}

export interface NoteCollaborationSettings {
  id: string;
  note_id: string;
  comments_enabled: boolean;
  track_changes_enabled: boolean;
  suggestions_mode: boolean;
  auto_save_interval: number;
  max_concurrent_editors: number;
  collaboration_mode: 'collaborative' | 'suggesting' | 'viewing';
  notification_settings?: Record<string, any> | null;
  updated_at: string;
}

export interface NoteCollaborationActivity {
  id: string;
  note_id: string;
  user_id: string;
  activity_type: string;
  activity_data?: Record<string, any> | null;
  user_name?: string;
  user_email?: string;
  created_at: string;
}

interface NoteCollaborationState {
  collaboratorsByNote: Record<string, NoteCollaborator[]>;
  commentsByNote: Record<string, NoteComment[]>;
  invitationsByNote: Record<string, NoteInvitation[]>;
  settingsByNote: Record<string, NoteCollaborationSettings | null>;
  activityByNote: Record<string, NoteCollaborationActivity[]>;
  loadingCollaboratorsByNote: Record<string, boolean>;
  loadingCommentsByNote: Record<string, boolean>;
  loadingInvitationsByNote: Record<string, boolean>;
  loadingSettingsByNote: Record<string, boolean>;
  loadingActivityByNote: Record<string, boolean>;
  error: string | null;

  getCollaborators: (noteId: string) => NoteCollaborator[];
  getComments: (noteId: string) => NoteComment[];
  getInvitations: (noteId: string) => NoteInvitation[];
  getSettings: (noteId: string) => NoteCollaborationSettings | null;
  getActivity: (noteId: string) => NoteCollaborationActivity[];
  isLoadingCollaborators: (noteId: string) => boolean;
  isLoadingComments: (noteId: string) => boolean;
  isLoadingInvitations: (noteId: string) => boolean;
  isLoadingSettings: (noteId: string) => boolean;
  isLoadingActivity: (noteId: string) => boolean;

  fetchCollaborationState: (noteId: string) => Promise<void>;
  fetchCollaborators: (noteId: string) => Promise<void>;
  addCollaborator: (noteId: string, userId: string, role: string) => Promise<void>;
  updateCollaboratorRole: (noteId: string, collaboratorUserId: string, role: string) => Promise<void>;
  removeCollaborator: (noteId: string, userId: string) => Promise<void>;

  fetchComments: (noteId: string) => Promise<void>;
  addComment: (noteId: string, commentText: string, options?: { parentCommentId?: string }) => Promise<void>;
  resolveComment: (noteId: string, commentId: string) => Promise<void>;

  fetchSettings: (noteId: string) => Promise<void>;
  updateSettings: (noteId: string, patch: Partial<NoteCollaborationSettings>) => Promise<void>;

  fetchInvitations: (noteId: string) => Promise<void>;
  inviteUser: (noteId: string, payload: {
    user_id: string;
    email?: string;
    role: 'editor' | 'reviewer' | 'viewer';
    phone?: string;
    message?: string;
    expires_in_hours?: number;
  }) => Promise<void>;
  revokeInvitation: (noteId: string, invitationId: string) => Promise<void>;
  acceptInvitation: (noteId: string, invitationId: string) => Promise<void>;

  fetchActivity: (noteId: string, limit?: number) => Promise<void>;

  clearError: () => void;
}

function normalizeComment(raw: any): NoteComment {
  const resolved = Boolean(raw?.resolved ?? raw?.is_resolved ?? false);
  return {
    ...raw,
    resolved,
    is_resolved: resolved,
  } as NoteComment;
}

function extractErrorMessage(err: any, fallback: string): string {
  return err?.response?.data?.message || err?.message || fallback;
}

export const useNoteCollaborationStore = create<NoteCollaborationState>((set, get) => ({
  collaboratorsByNote: {},
  commentsByNote: {},
  invitationsByNote: {},
  settingsByNote: {},
  activityByNote: {},
  loadingCollaboratorsByNote: {},
  loadingCommentsByNote: {},
  loadingInvitationsByNote: {},
  loadingSettingsByNote: {},
  loadingActivityByNote: {},
  error: null,

  getCollaborators: (noteId: string) => get().collaboratorsByNote[noteId] || [],
  getComments: (noteId: string) => get().commentsByNote[noteId] || [],
  getInvitations: (noteId: string) => get().invitationsByNote[noteId] || [],
  getSettings: (noteId: string) => get().settingsByNote[noteId] || null,
  getActivity: (noteId: string) => get().activityByNote[noteId] || [],
  isLoadingCollaborators: (noteId: string) => Boolean(get().loadingCollaboratorsByNote[noteId]),
  isLoadingComments: (noteId: string) => Boolean(get().loadingCommentsByNote[noteId]),
  isLoadingInvitations: (noteId: string) => Boolean(get().loadingInvitationsByNote[noteId]),
  isLoadingSettings: (noteId: string) => Boolean(get().loadingSettingsByNote[noteId]),
  isLoadingActivity: (noteId: string) => Boolean(get().loadingActivityByNote[noteId]),

  fetchCollaborationState: async (noteId: string) => {
    set((state) => ({
      error: null,
      loadingCollaboratorsByNote: {
        ...state.loadingCollaboratorsByNote,
        [noteId]: true,
      },
      loadingSettingsByNote: {
        ...state.loadingSettingsByNote,
        [noteId]: true,
      },
      loadingInvitationsByNote: {
        ...state.loadingInvitationsByNote,
        [noteId]: true,
      },
    }));

    try {
      const response = await apiClient.get(`/notes/${noteId}/collaboration`);
      const state = response?.data?.data || {};

      set((current) => ({
        collaboratorsByNote: {
          ...current.collaboratorsByNote,
          [noteId]: state.collaborators || [],
        },
        settingsByNote: {
          ...current.settingsByNote,
          [noteId]: state.settings || null,
        },
        invitationsByNote: {
          ...current.invitationsByNote,
          [noteId]: state.invitations || [],
        },
        loadingCollaboratorsByNote: {
          ...current.loadingCollaboratorsByNote,
          [noteId]: false,
        },
        loadingSettingsByNote: {
          ...current.loadingSettingsByNote,
          [noteId]: false,
        },
        loadingInvitationsByNote: {
          ...current.loadingInvitationsByNote,
          [noteId]: false,
        },
      }));
    } catch (err: any) {
      set((state) => ({
        error: extractErrorMessage(err, 'Failed to fetch collaboration state'),
        loadingCollaboratorsByNote: {
          ...state.loadingCollaboratorsByNote,
          [noteId]: false,
        },
        loadingSettingsByNote: {
          ...state.loadingSettingsByNote,
          [noteId]: false,
        },
        loadingInvitationsByNote: {
          ...state.loadingInvitationsByNote,
          [noteId]: false,
        },
      }));
    }
  },

  fetchCollaborators: async (noteId: string) => {
    set((state) => ({
      error: null,
      loadingCollaboratorsByNote: {
        ...state.loadingCollaboratorsByNote,
        [noteId]: true,
      },
    }));
    try {
      const response = await apiClient.get(`/notes/${noteId}/collaborators`);
      set((state) => ({
        collaboratorsByNote: {
          ...state.collaboratorsByNote,
          [noteId]: response.data?.data?.collaborators || [],
        },
        loadingCollaboratorsByNote: {
          ...state.loadingCollaboratorsByNote,
          [noteId]: false,
        },
      }));
    } catch (err: any) {
      set((state) => ({
        error: extractErrorMessage(err, 'Failed to fetch collaborators'),
        loadingCollaboratorsByNote: {
          ...state.loadingCollaboratorsByNote,
          [noteId]: false,
        },
      }));
    }
  },

  addCollaborator: async (noteId: string, userId: string, role: string) => {
    try {
      await apiClient.post(`/notes/${noteId}/collaborators`, { user_id: userId, role });
      await get().fetchCollaborators(noteId);
    } catch (err: any) {
      set({ error: extractErrorMessage(err, 'Failed to add collaborator') });
    }
  },

  updateCollaboratorRole: async (noteId: string, collaboratorUserId: string, role: string) => {
    try {
      await apiClient.put(`/notes/${noteId}/collaborators/${collaboratorUserId}`, { role });
      await get().fetchCollaborators(noteId);
    } catch (err: any) {
      set({ error: extractErrorMessage(err, 'Failed to update collaborator role') });
    }
  },

  removeCollaborator: async (noteId: string, userId: string) => {
    try {
      await apiClient.delete(`/notes/${noteId}/collaborators/${userId}`);
      set((state) => ({
        collaboratorsByNote: {
          ...state.collaboratorsByNote,
          [noteId]: (state.collaboratorsByNote[noteId] || []).filter((c) => c.user_id !== userId),
        },
      }));
    } catch (err: any) {
      set({ error: extractErrorMessage(err, 'Failed to remove collaborator') });
    }
  },

  fetchComments: async (noteId: string) => {
    set((state) => ({
      error: null,
      loadingCommentsByNote: {
        ...state.loadingCommentsByNote,
        [noteId]: true,
      },
    }));
    try {
      const response = await apiClient.get(`/notes/${noteId}/comments`);
      const normalized = (response.data?.data?.comments || []).map(normalizeComment);
      set((state) => ({
        commentsByNote: {
          ...state.commentsByNote,
          [noteId]: normalized,
        },
        loadingCommentsByNote: {
          ...state.loadingCommentsByNote,
          [noteId]: false,
        },
      }));
    } catch (err: any) {
      set((state) => ({
        error: extractErrorMessage(err, 'Failed to fetch comments'),
        loadingCommentsByNote: {
          ...state.loadingCommentsByNote,
          [noteId]: false,
        },
      }));
    }
  },

  addComment: async (noteId: string, commentText: string, options?: { parentCommentId?: string }) => {
    try {
      const response = await apiClient.post(`/notes/${noteId}/comments`, {
        comment_text: commentText,
        parent_comment_id: options?.parentCommentId,
      });
      await get().fetchComments(noteId);
      noteCollaborationSocket.emitCommentAdded(noteId, response?.data?.data?.id);
    } catch (err: any) {
      set({ error: extractErrorMessage(err, 'Failed to add comment') });
    }
  },

  resolveComment: async (noteId: string, commentId: string) => {
    try {
      await apiClient.put(`/notes/${noteId}/comments/${commentId}/resolve`);
      set((state) => ({
        commentsByNote: {
          ...state.commentsByNote,
          [noteId]: (state.commentsByNote[noteId] || []).map((comment) => (
            comment.id === commentId
              ? { ...comment, resolved: true, is_resolved: true }
              : comment
          )),
        },
      }));
      noteCollaborationSocket.emitCommentResolved(noteId, commentId);
    } catch (err: any) {
      set({ error: extractErrorMessage(err, 'Failed to resolve comment') });
    }
  },

  fetchSettings: async (noteId: string) => {
    set((state) => ({
      error: null,
      loadingSettingsByNote: {
        ...state.loadingSettingsByNote,
        [noteId]: true,
      },
    }));

    try {
      const response = await apiClient.get(`/notes/${noteId}/collaboration`);
      set((state) => ({
        settingsByNote: {
          ...state.settingsByNote,
          [noteId]: response?.data?.data?.settings || null,
        },
        loadingSettingsByNote: {
          ...state.loadingSettingsByNote,
          [noteId]: false,
        },
      }));
    } catch (err: any) {
      set((state) => ({
        error: extractErrorMessage(err, 'Failed to fetch collaboration settings'),
        loadingSettingsByNote: {
          ...state.loadingSettingsByNote,
          [noteId]: false,
        },
      }));
    }
  },

  updateSettings: async (noteId: string, patch: Partial<NoteCollaborationSettings>) => {
    try {
      const response = await apiClient.put(`/notes/${noteId}/collaboration`, patch);
      set((state) => ({
        settingsByNote: {
          ...state.settingsByNote,
          [noteId]: response?.data?.data || state.settingsByNote[noteId] || null,
        },
      }));
    } catch (err: any) {
      set({ error: extractErrorMessage(err, 'Failed to update collaboration settings') });
    }
  },

  fetchInvitations: async (noteId: string) => {
    set((state) => ({
      error: null,
      loadingInvitationsByNote: {
        ...state.loadingInvitationsByNote,
        [noteId]: true,
      },
    }));

    try {
      const response = await apiClient.get(`/notes/${noteId}/invitations`);
      set((state) => ({
        invitationsByNote: {
          ...state.invitationsByNote,
          [noteId]: response?.data?.data?.invitations || [],
        },
        loadingInvitationsByNote: {
          ...state.loadingInvitationsByNote,
          [noteId]: false,
        },
      }));
    } catch (err: any) {
      set((state) => ({
        error: extractErrorMessage(err, 'Failed to fetch invitations'),
        loadingInvitationsByNote: {
          ...state.loadingInvitationsByNote,
          [noteId]: false,
        },
      }));
    }
  },

  inviteUser: async (noteId: string, payload: {
    user_id: string;
    email?: string;
    role: 'editor' | 'reviewer' | 'viewer';
    phone?: string;
    message?: string;
    expires_in_hours?: number;
  }) => {
    try {
      await apiClient.post(`/notes/${noteId}/collaborators/invite`, payload);
      await Promise.all([
        get().fetchInvitations(noteId),
        get().fetchCollaborators(noteId),
      ]);
    } catch (err: any) {
      set({ error: extractErrorMessage(err, 'Failed to invite collaborator') });
      throw err;
    }
  },

  revokeInvitation: async (noteId: string, invitationId: string) => {
    try {
      await apiClient.delete(`/notes/${noteId}/invitations/${invitationId}`);
      set((state) => ({
        invitationsByNote: {
          ...state.invitationsByNote,
          [noteId]: (state.invitationsByNote[noteId] || []).map((invitation) => (
            invitation.id === invitationId
              ? { ...invitation, status: 'revoked' }
              : invitation
          )),
        },
      }));
    } catch (err: any) {
      set({ error: extractErrorMessage(err, 'Failed to revoke invitation') });
    }
  },

  acceptInvitation: async (noteId: string, invitationId: string) => {
    try {
      await apiClient.post(`/notes/${noteId}/invitations/${invitationId}/accept`);
      await Promise.all([
        get().fetchInvitations(noteId),
        get().fetchCollaborators(noteId),
      ]);
    } catch (err: any) {
      set({ error: extractErrorMessage(err, 'Failed to accept invitation') });
      throw err;
    }
  },

  fetchActivity: async (noteId: string, limit = 50) => {
    set((state) => ({
      error: null,
      loadingActivityByNote: {
        ...state.loadingActivityByNote,
        [noteId]: true,
      },
    }));

    try {
      const response = await apiClient.get(`/notes/${noteId}/activity`, {
        params: { limit },
      });
      set((state) => ({
        activityByNote: {
          ...state.activityByNote,
          [noteId]: response?.data?.data?.activities || [],
        },
        loadingActivityByNote: {
          ...state.loadingActivityByNote,
          [noteId]: false,
        },
      }));
    } catch (err: any) {
      set((state) => ({
        error: extractErrorMessage(err, 'Failed to fetch collaboration activity'),
        loadingActivityByNote: {
          ...state.loadingActivityByNote,
          [noteId]: false,
        },
      }));
    }
  },

  clearError: () => set({ error: null }),
}));
